import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CxmMethod,
  CxmPayoutAccount,
  CxmPayoutAccountRequirements,
  methodLimits,
  validateAmount,
  validatePayoutDetails,
  visibleFields,
} from "src/api";
import { CxmDepositOutcome, CxmDepositState, CxmWithdrawalState } from "src/hooks";
import { formatNumberToExchange, onlyNumbers } from "src/utils";
import {
  DEPOSIT_METHODS,
  LATENCY,
  REQUIREMENTS,
  SEED_ACCOUNTS,
  WITHDRAWAL_METHODS,
  sleep,
} from "./mockGateway";

/**
 * Which answer the fake gateway gives to the catalogue call. `empty` and
 * `failed` are not hypotheticals — an operator who granted no payment rules
 * produces the first, and both terminal screens ship, so both need a way in.
 */
export type Scenario = "normal" | "empty" | "failed";

/** What the demo balance says, so `Max` has something honest to compute from. */
const BALANCE = 1274.51;
const CURRENCY = { currency_id: "USD", precision: 2 };

const CATALOGUE_ERROR = "HTTP 502: Bad Gateway";

/**
 * The demo opens on the most complicated schema it has — a select driving a
 * dependent select — because that is the screen worth looking at. Opening on
 * the gateway's own first method showed a single text field, which is what
 * shipped before selects existed, so nothing appeared to have changed.
 */
const openingMethodId = (methods: CxmMethod[]): string | null => {
  const complex = methods.find((m) => {
    const fields = m.paymentMethodTypeId
      ? (REQUIREMENTS[m.paymentMethodTypeId]?.fields ?? [])
      : [];
    return fields.some((f) => f.kind === "select" && f.dependsOn);
  });
  return (complex ?? methods[0])?.methodId ?? null;
};

/**
 * The payout orchestrator with the network swapped for fixtures. Everything
 * else — limit checks, the truncating `Max`, the required-field check that
 * exists because CXM validates nothing — is the shipped code, imported from
 * `src/api`, so what the demo accepts is what production accepts.
 *
 * Deliberately not a wrapper around `useCxmWithdrawal`: that hook reads the
 * session contexts and calls `cxmService` directly, so driving it here would
 * mean a real token and a reachable gateway, which is the thing this page
 * exists to avoid.
 */
export const useCxmMockWithdrawal = (scenario: Scenario, run: number): CxmWithdrawalState => {
  const methods = useMemo(
    () => (scenario === "normal" ? WITHDRAWAL_METHODS : []),
    [scenario]
  );

  const [methodsLoading, setMethodsLoading] = useState(true);
  const [methodsError, setMethodsError] = useState<string | null>(null);
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);

  const [accountStore, setAccountStore] = useState<CxmPayoutAccount[]>(SEED_ACCOUNTS);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);

  const [requirements, setRequirements] = useState<CxmPayoutAccountRequirements | null>(null);
  const [requirementsLoading, setRequirementsLoading] = useState(false);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [addAccountLoading, setAddAccountLoading] = useState(false);
  const [addAccountError, setAddAccountError] = useState<string | null>(null);

  const [amount, setAmount] = useState("");
  const [amountTouched, setAmountTouched] = useState(false);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<{ amount: string; status: string } | null>(null);

  /* Replaying restores the opening state, catalogue latency included. */
  useEffect(() => {
    let cancelled = false;
    setMethodsLoading(true);
    setMethodsError(null);
    setSelectedMethodId(null);
    setAccountStore(SEED_ACCOUNTS);
    setSelectedAccountId(null);
    setAmount("");
    setAmountTouched(false);
    setSubmitted(null);
    setSubmitError(null);
    setAddAccountOpen(false);

    sleep(LATENCY.catalogue).then(() => {
      if (cancelled) return;
      setMethodsError(scenario === "failed" ? CATALOGUE_ERROR : null);
      setSelectedMethodId(openingMethodId(methods));
      setMethodsLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario, run]);

  const selectedMethod = useMemo(
    () => methods.find((m) => m.methodId === selectedMethodId) ?? null,
    [methods, selectedMethodId]
  );

  const typeId = selectedMethod?.paymentMethodTypeId;

  /* The bank rail has no saved account on purpose, so the schema-built form
     is the screen the demo lands on rather than one hidden behind a click. */
  useEffect(() => {
    if (!typeId) return;
    const hasAccount = accountStore.some((a) => a.paymentMethodTypeId === typeId);
    setAddAccountOpen(!hasAccount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId, run]);

  /* Picking a method reloads its schema and its accounts, as the real one does. */
  useEffect(() => {
    if (!typeId) return undefined;
    let cancelled = false;

    setRequirements(null);
    setRequirementsLoading(true);
    setAccountsLoading(true);

    sleep(LATENCY.accounts).then(() => {
      if (cancelled) return;
      setAccountsLoading(false);
      setSelectedAccountId((current) => {
        const usable = accountStore.filter((a) => a.paymentMethodTypeId === typeId);
        return usable.some((a) => a.id === current) ? current : (usable[0]?.id ?? null);
      });
    });

    sleep(LATENCY.schema).then(() => {
      if (cancelled) return;
      setRequirements(REQUIREMENTS[typeId] ?? null);
      setRequirementsLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeId, run]);

  const accounts = useMemo(
    () => (typeId ? accountStore.filter((a) => a.paymentMethodTypeId === typeId) : []),
    [accountStore, typeId]
  );

  const amountError = useMemo(
    () => (selectedMethod ? validateAmount(selectedMethod, amount || "0") : undefined) ?? undefined,
    [selectedMethod, amount]
  );

  const limits = useMemo(
    () => (selectedMethod ? methodLimits(selectedMethod) : { min: null, max: null }),
    [selectedMethod]
  );

  const maxSendable = useMemo(
    () => (limits.max === null ? BALANCE : Math.min(BALANCE, limits.max)),
    [limits.max]
  );

  const handleAmountChange = useCallback((value: string) => {
    setAmountTouched(true);
    if (!onlyNumbers(value)) return;
    setAmount(formatNumberToExchange(value, Math.max(CURRENCY.precision, 2)));
  }, []);

  const handleMaxAmount = useCallback(() => {
    setAmountTouched(true);
    const factor = 10 ** Math.max(CURRENCY.precision, 2);
    const truncated = Math.floor(maxSendable * factor) / factor;
    setAmount(formatNumberToExchange(String(truncated), Math.max(CURRENCY.precision, 2)));
  }, [maxSendable]);

  const handleAddAccount = useCallback(
    async (details: Record<string, string>) => {
      if (!typeId || !requirements) return;

      const fieldErrors = validatePayoutDetails(requirements, details);
      if (Object.keys(fieldErrors).length) {
        setAddAccountError(Object.values(fieldErrors)[0]);
        return;
      }

      setAddAccountLoading(true);
      setAddAccountError(null);
      await sleep(LATENCY.createAccount);

      const created: CxmPayoutAccount = {
        id: Math.max(0, ...accountStore.map((a) => a.id)) + 1,
        paymentMethodTypeId: typeId,
        type: requirements.type,
        status: "approved",
        details,
        createdAt: new Date().toISOString(),
      };
      setAccountStore((prev) => [...prev, created]);
      setSelectedAccountId(created.id);
      setAddAccountLoading(false);
      setAddAccountOpen(false);
    },
    [typeId, requirements, accountStore]
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!selectedMethod || !selectedAccountId) return;
      setSubmitLoading(true);
      setSubmitError(null);
      await sleep(LATENCY.submit);
      setSubmitted({ amount, status: "processing" });
      setSubmitLoading(false);
    },
    [selectedMethod, selectedAccountId, amount]
  );

  const reset = useCallback(() => {
    setSubmitted(null);
    setAmount("");
    setAmountTouched(false);
    setSubmitError(null);
  }, []);

  return {
    currency: CURRENCY,
    balance: BALANCE,
    methods,
    methodsLoading,
    methodsError,
    selectedMethod,
    selectedMethodId,
    setSelectedMethodId,

    accounts,
    accountsLoading,
    selectedAccountId,
    setSelectedAccountId,
    requirements,
    requirementFields: requirements ? visibleFields(requirements) : [],
    requirementsLoading,
    requirementsError: null,

    addAccountOpen,
    setAddAccountOpen,
    addAccountLoading,
    addAccountError,
    handleAddAccount,

    amount,
    amountTouched,
    amountError,
    limits,
    maxSendable,
    handleAmountChange,
    handleMaxAmount,

    submitDisabled:
      submitLoading || !selectedMethod || !selectedAccountId || !amount || Boolean(amountError),
    submitLoading,
    submitError,
    submitted,
    handleSubmit,
    reset,
  };
};

/**
 * The deposit orchestrator, same treatment. `redirect` is the one branch the
 * demo cannot honour: in production it navigates to `/payment-redirect`, which
 * nests the gateway's checkout in an inner iframe — there is no checkout to
 * nest here. Every method the test environment grants us answers `none`
 * anyway, so the confirmed screen is the real ending today.
 */
export const useCxmMockDeposit = (scenario: Scenario, run: number): CxmDepositState => {
  const methods = useMemo(() => (scenario === "normal" ? DEPOSIT_METHODS : []), [scenario]);

  const [methodsLoading, setMethodsLoading] = useState(true);
  const [methodsError, setMethodsError] = useState<string | null>(null);
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);

  const [amount, setAmount] = useState("");
  const [amountTouched, setAmountTouched] = useState(false);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<CxmDepositOutcome | null>(null);

  useEffect(() => {
    let cancelled = false;
    setMethodsLoading(true);
    setMethodsError(null);
    setSelectedMethodId(null);
    setAmount("");
    setAmountTouched(false);
    setOutcome(null);
    setSubmitError(null);

    sleep(LATENCY.catalogue).then(() => {
      if (cancelled) return;
      setMethodsError(scenario === "failed" ? CATALOGUE_ERROR : null);
      setSelectedMethodId(methods[0]?.methodId ?? null);
      setMethodsLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenario, run]);

  const selectedMethod = useMemo(
    () => methods.find((m) => m.methodId === selectedMethodId) ?? null,
    [methods, selectedMethodId]
  );

  const amountError = useMemo(
    () => (selectedMethod ? validateAmount(selectedMethod, amount || "0") : undefined) ?? undefined,
    [selectedMethod, amount]
  );

  const limits = useMemo(
    () => (selectedMethod ? methodLimits(selectedMethod) : { min: null, max: null }),
    [selectedMethod]
  );

  const handleAmountChange = useCallback((value: string) => {
    setAmountTouched(true);
    if (!onlyNumbers(value)) return;
    setAmount(formatNumberToExchange(value, 2));
  }, []);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!selectedMethod) return;
      setSubmitLoading(true);
      setSubmitError(null);
      await sleep(LATENCY.submit);
      setOutcome({ kind: "confirmed", amount, status: "pending" });
      setSubmitLoading(false);
    },
    [selectedMethod, amount]
  );

  const reset = useCallback(() => {
    setOutcome(null);
    setAmount("");
    setAmountTouched(false);
    setSubmitError(null);
  }, []);

  return {
    currency: selectedMethod?.currency ?? "",
    methods,
    methodsLoading,
    methodsError,
    selectedMethod,
    selectedMethodId,
    setSelectedMethodId,

    amount,
    amountTouched,
    amountError,
    limits,
    handleAmountChange,

    submitDisabled: submitLoading || !selectedMethod || !amount || Boolean(amountError),
    submitLoading,
    submitError,
    outcome,
    handleSubmit,
    reset,
  };
};
